import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Layout from "../components/Layout";
import AddState from "../components/AddState";
import { addStateDocusignService } from "../services/docusign.service";
import AgentService from "../services/agent.service";
import { useAuth } from "../hooks/useAuth";

// #19 - Landing page after signing the Add State form. The agent gets here by a
// full-page redirect from DocuSign (real) or the mock page, so the in-memory
// state is gone. We confirm the envelope status and, if complete, rehydrate the
// Add State flow from the stored payload and drop the agent on Review. If not
// complete (cancelled, pending), we offer to resume.
const REVIEW_STEP = 4;

const AddStateSigningReturn = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ loading: true });
  const [resuming, setResuming] = useState(false);
  // #57 - a principal returning from a signing they drove on an agent's behalf
  // (?agent= rides the DocuSign return URL). Re-fetch that agent's prefill so
  // the Review step shows the AGENT's identity, not the principal's.
  const onBehalfAgentId =
    user?.role === "agency" ? searchParams.get("agent") : null;
  const [agentOverride, setAgentOverride] = useState(null);
  useEffect(() => {
    if (!onBehalfAgentId) return;
    AgentService.prefillFor(onBehalfAgentId)
      .then(setAgentOverride)
      .catch(() => {});
  }, [onBehalfAgentId]);

  useEffect(() => {
    addStateDocusignService
      .getStatus(id)
      .then((res) => setState({ loading: false, ...res }))
      .catch(() =>
        setState({ loading: false, error: "We couldn't confirm your signature." })
      );
  }, [id]);

  const resumeSigning = () => {
    setResuming(true);
    addStateDocusignService
      .startSigning({ unsignedId: id, ...(state.payload || {}) })
      .then(({ signingUrl }) => {
        window.location.href = signingUrl;
      })
      .catch(() => setResuming(false));
  };

  if (state.loading) {
    return (
      <Layout>
        <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
          <Stack spacing={2} alignItems="center" sx={{ mt: 6 }}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Confirming your signature...
            </Typography>
          </Stack>
        </Box>
      </Layout>
    );
  }

  // Signed: rehydrate the flow and continue at Review.
  if (state.completed) {
    return (
      <Layout>
        <Box sx={{ padding: 4 }}>
          <AddState
            initialData={state.payload}
            initialStep={REVIEW_STEP}
            unsignedId={id}
            agentOverride={agentOverride}
          />
        </Box>
      </Layout>
    );
  }

  // Not signed (cancelled, still pending): offer to resume.
  return (
    <Layout>
      <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
        <Paper sx={{ p: 4, maxWidth: 560, textAlign: "center" }} elevation={2}>
          <Stack spacing={2} alignItems="center">
            <Alert severity="warning" sx={{ width: "100%" }}>
              {state.error || "Your add-state form isn't signed yet."}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              You need to sign your add-state form before you can submit your
              request.
            </Typography>
            <Button
              variant="contained"
              size="large"
              disabled={resuming}
              onClick={resumeSigning}
              startIcon={resuming ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {resuming ? "Taking you to DocuSign..." : "Resume signing"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Layout>
  );
};

export default AddStateSigningReturn;
